import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import refreshListings from 'app-core/platform/listable/refreshListings';
import isArchived from 'archive/isArchived';
import {Permissions} from 'auth/PermissionConstants';
import {TestOrderStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import BulkUpdateTestOrderContainerModal from 'sample-tests/ui/test-orders/modals/BulkUpdateTestOrderContainerModal';
import BulkUpdateTestOrderStatusModal from 'sample-tests/ui/test-orders/modals/BulkUpdateTestOrderStatusModal';
import {ListableItem} from 'search/SearchConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import runModal from 'ui-library/runModal';

import {TestOrderListingGraphQL} from '../../../../graphql-fragments/sample_tests/__generated/TestOrderListingGraphQL';

type TestOrderListingGraphQLWithPermissions = TestOrderListingGraphQL & {permissions: Permissions};

function checkAllTestOrdersShareSample(items: Array<TestOrderListingGraphQLWithPermissions>): boolean {
  return items.length > 0 && items.every(({sample}) => sample.id === items[0].sample.id);
}

function checkAllTestOrdersHaveUnarchivedSample(
  items: Array<TestOrderListingGraphQLWithPermissions>
): boolean {
  return items.every(({sample}) => !isArchived(sample));
}

function checkAllTestOrdersArePending(items: Array<TestOrderListingGraphQLWithPermissions>): boolean {
  return items.every(({status}) => status === TestOrderStatus.PENDING);
}

function checkAllItemsEditStatusAccessible(items: Array<TestOrderListingGraphQLWithPermissions>): boolean {
  return items.every((item) => item.permissions.canEditStatus);
}

function checkAllItemsEditContainerAccessible(items: Array<TestOrderListingGraphQLWithPermissions>): boolean {
  return items.every((item) => item.permissions.canEditContainer);
}

interface TestOrderEditorDropdownProps {
  items: Array<ListableItem>;
  /** Search session id; provided from study items page to allow refreshing that listing. */
  searchSessionId?: string;
}

export default function TestOrdersEditorDropdown(props: TestOrderEditorDropdownProps): JSX.Element {
  const itemsWithoutPermissionsCheck = props.items as Array<TestOrderListingGraphQLWithPermissions>;

  const afterSubmitCallback = (): void => {
    refreshListings(props.searchSessionId != null ? [props.searchSessionId] : undefined);
  };

  const runBulkUpdateTestOrderStatusModal = async (): Promise<void> => {
    await runModal(BulkUpdateTestOrderStatusModal, {
      testOrders: itemsWithoutPermissionsCheck,
      onRequestSubmit: afterSubmitCallback,
    });
  };

  const runBulkUpdateTestOrderContainerModal = async (): Promise<void> => {
    await runModal(BulkUpdateTestOrderContainerModal, {
      testOrders: itemsWithoutPermissionsCheck,
      onRequestSubmit: afterSubmitCallback,
    });
  };

  const allTestOrdersShareSample = checkAllTestOrdersShareSample(itemsWithoutPermissionsCheck);
  const allTestOrdersArePending = checkAllTestOrdersArePending(itemsWithoutPermissionsCheck);
  const allItemsHaveUnarchivedSample = checkAllTestOrdersHaveUnarchivedSample(itemsWithoutPermissionsCheck);
  const allItemsEditStatusAccessible = checkAllItemsEditStatusAccessible(itemsWithoutPermissionsCheck);
  const allItemsEditContainerAccessible = checkAllItemsEditContainerAccessible(itemsWithoutPermissionsCheck);

  const menu = (
    <Menu>
      <Option
        className={css(styles.option)}
        value="status"
        onSelect={runBulkUpdateTestOrderStatusModal}
        isDisabled={!allItemsEditStatusAccessible || !allItemsHaveUnarchivedSample}
        tooltipTitle={
          !allItemsHaveUnarchivedSample
            ? 'You can not update the status for test orders with archived samples'
            : !allItemsEditStatusAccessible
              ? 'You do not have access to update the status for all selected test orders'
              : undefined
        }
      >
        Update status
      </Option>
      <Option
        className={css(styles.option)}
        value="container"
        onSelect={runBulkUpdateTestOrderContainerModal}
        isDisabled={
          !allTestOrdersShareSample ||
          !allTestOrdersArePending ||
          !allItemsEditContainerAccessible ||
          !allItemsHaveUnarchivedSample
        }
        tooltipTitle={
          !allItemsHaveUnarchivedSample
            ? 'You can not update the container for test orders with archived samples'
            : !allItemsEditContainerAccessible
              ? 'You do not have access to update the container for all selected test orders'
              : !allTestOrdersArePending
                ? 'Container can only be updated for pending test orders'
                : !allTestOrdersShareSample
                  ? 'All selected test orders must have the same sample to update container'
                  : undefined
        }
      >
        Update container
      </Option>
    </Menu>
  );
  return (
    <Dropdown menu={menu}>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_WHITE}
        icon="edit"
        isDropdown={true}
        size={ButtonSize.SMALL}
        dataTestAttrs={{component: 'TestOrderEditorDropdown', element: 'editButton'}}
      />
    </Dropdown>
  );
}

const styles = StyleSheet.create({
  option: {
    width: '132px',
  },
});
